import { canCreateCharacter } from '@sorrem/shared';
import { useCurrentPlayerCharacter } from '@sorrem-client/ui/hooks/use-current-player-character';
import type { UIPartyCharacter } from '@sorrem-client/types';
import { useMemo } from 'react';

export interface CharacterRosterEntry {
    readonly id: string;
    readonly name: string;
    readonly isSelected: boolean;
    /** Veil Attunement, Crafting and Martial Discipline, in whole percent (Crafting has not awakened, so 0). */
    readonly attunementPercent: number;
    readonly craftingPercent: number;
    readonly martialDisciplinePercent: number;
}

interface CharacterRosterState {
    readonly entries: readonly CharacterRosterEntry[];
    /** False once the account has reached its character limit. */
    readonly canCreateCharacter: boolean;
    readonly isDeletingCharacter: boolean;
    readonly selectCharacter: (characterId: string) => Promise<boolean>;
    readonly deleteCharacter: (characterId: string) => Promise<boolean>;
}

/** The account's characters as selector rows: each one's own mastery, as the shared rules give it (#330). */
export function toCharacterRosterEntries(characters: readonly UIPartyCharacter[], selectedId: string | null): CharacterRosterEntry[] {
    return characters.map((entry) => ({
        id: entry.id,
        name: entry.name,
        isSelected: entry.id === selectedId,
        attunementPercent: Math.round(entry.disciplines.veilAttunement),
        // Crafting has not awakened in alpha.
        craftingPercent: 0,
        martialDisciplinePercent: Math.round(entry.disciplines.martialDiscipline),
    }));
}

/** Returns the account's characters as selector rows, plus the actions to manage them. */
export function useCharacterRoster(): CharacterRosterState {
    const {
        accountProfile,
        characters,
        character,
        isDeletingCharacter,
        selectCharacter,
        deleteCharacter,
    } = useCurrentPlayerCharacter();

    const entries = useMemo(() => toCharacterRosterEntries(characters, character?.id ?? null), [character?.id, characters]);

    const maxCharacters = accountProfile?.accountTypeConfig.maxCharacters;
    const isUnderCharacterLimit = maxCharacters !== undefined && canCreateCharacter(maxCharacters, characters.length);

    return {
        entries,
        canCreateCharacter: isUnderCharacterLimit,
        isDeletingCharacter,
        selectCharacter,
        deleteCharacter,
    };
}
